import Constants from "expo-constants";
import i18next, { t } from "i18next";
import { DateTime } from "luxon";
//@ts-ignore
import numeral from "numeral";
import pluralize from "pluralize";
import { isWeb } from "./platform";

export const formatCount = (text: string, count: number, reverse?: boolean) =>
	reverse
		? `${count} ${text}${count !== 1 ? "s" : ""}`
		: `${text}${count !== 1 ? "s" : ""}`;

export const formatNumber = (num: number): string =>
	num?.toLocaleString("en-US");

export const convertToMinutes = (seconds: number) => Math.ceil(seconds / 60);

export const formatSecondsToUTCtime = (seconds: number) => {
	const date = new Date(seconds * 1000);
	const hr = date.getUTCHours();
	const min = String(date.getUTCMinutes()).padStart(2, "0");
	const sec = String(date.getUTCSeconds()).padStart(2, "0");
	return hr ? `${hr}:${min}:${sec}` : `${min}:${sec}`;
};

export const formatRank = (rank: number) => {
	if (!rank) {
		return null;
	}
	switch (rank) {
		case 1:
			return `${rank}st`;
		case 2:
			return `${rank}nd`;
		case 3:
			return `${rank}rd`;
		default:
			return `${rank}`;
	}
};

export const titleCase = (s: string) => {
	const [c, ...rest] = s.split("");
	return `${c?.toUpperCase()}${rest.join("")}`;
};

export const getCurrentDate = () => {
	return new Date().toLocaleDateString();
};

export const formatNumberWithAbbreviation = (num: number): string => {
	return numeral(num).format("0.[0]a");
};

export function smartPluralize(
	singular: string,
	count: number,
	options?: { language?: string },
) {
	const language = options?.language || i18next.language;
	if (language === "en-US") {
		return pluralize(t(`common.${singular}`), count);
	}
	return t(`common.${singular}`, { count });
}

const isExpoGoRuntime = () => {
	const isExpoGo =
		Constants.executionEnvironment === "storeClient" ||
		Constants.appOwnership === "expo";
	return isExpoGo;
};

export const isNativeModulesDisabled = (): boolean =>
	isWeb || isExpoGoRuntime();

export const isNativeStorageDisabled = (): boolean => isNativeModulesDisabled();

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;

/** Format seconds into a human-readable duration (e.g. "2h 30m", "15m", "< 1m"). */
export const formatSecondsToDurationLabel = (seconds: number): string => {
	const hours = Math.floor(seconds / SECONDS_PER_HOUR);
	const minutes = Math.floor((seconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
	if (hours > 0) {
		return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
	}
	if (minutes === 0) {
		return "< 1m";
	}
	return `${minutes}m`;
};

/** Format an ISO or SQL date string to "25 MAR 2024" style. */
export const formatDateToDayMonthYear = (value: string): string => {
	const dt = DateTime.fromISO(value, { zone: "utc" });
	if (dt.isValid) {
		return dt.toFormat("d MMM yyyy").toUpperCase();
	}
	const dtSql = DateTime.fromSQL(value, { zone: "utc" });
	if (dtSql.isValid) {
		return dtSql.toFormat("d MMM yyyy").toUpperCase();
	}
	return value;
};

/** Format seconds into MM:SS or HH:MM:SS for playback display. */
export const formatSecondsToTimestamp = (seconds: number): string => {
	const totalSeconds = Math.max(0, Math.floor(seconds));
	const hrs = Math.floor(totalSeconds / SECONDS_PER_HOUR);
	const mins = Math.floor(
		(totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE,
	);
	const secs = totalSeconds % SECONDS_PER_MINUTE;
	if (hrs > 0) {
		return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
	}
	return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export const SAFE_SCHEMES = ["https", "mailto", "tel"] as const;

export const isSafeUrl = (
	url?: string,
	{
		allowedSchemes = SAFE_SCHEMES as readonly string[],
	}: { allowedSchemes?: readonly string[] } = {},
): boolean => {
	if (!url) {
		return false;
	}
	const schemeMatch = url.match(/^([a-z][a-z0-9+\-.]*):./i);
	if (!schemeMatch) {
		return false;
	}
	const scheme = schemeMatch[1].toLowerCase();
	return allowedSchemes.some((s) => s.toLowerCase() === scheme);
};
